// src/utils/mockDataTravel.ts — Fatigue Management, Travel & Accommodation data
// Based on TrafficProcure Fatigue Management Specification v1.0
// Regulatory basis: WHS Act 2011 (QLD) · Safe Work Australia Code of Practice (Sep 2025)

// ─────────────────────────────────────────────────────────────────
// FM RISK LEVELS & THRESHOLDS (env-configurable in production)
// ─────────────────────────────────────────────────────────────────
export const FM_THRESHOLDS = {
  DISTANCE_YELLOW_KM:  50,   // triggers YELLOW
  DISTANCE_AMBER_KM:  100,   // triggers AMBER
  DAYS_AMBER:           3,   // consecutive cross-region days → AMBER
  DAYS_RED:             2,   // consecutive >100km days → RED
  CONSECUTIVE_MAX:      6,   // mandatory rest day at this point
  MIN_REST_HOURS:      10,   // minimum rest between night shift and next shift
}

export const FM_RISK_META: Record<string, { label: string; bg: string; text: string; border: string; dot: string; icon: string }> = {
  GREEN:  { label: 'Green — no flag',        bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200', dot: 'bg-green-500',  icon: '✓' },
  YELLOW: { label: 'Yellow — PM notified',   bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300',dot: 'bg-yellow-500', icon: '⚠' },
  AMBER:  { label: 'Amber — action required',bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-400', dot: 'bg-amber-500',  icon: '⚠' },
  RED:    { label: 'Red — roster blocked',   bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-400',   dot: 'bg-red-600',    icon: '✕' },
}

export const FM_STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  OPEN:         { label: 'Open — action required', bg: 'bg-red-100',    text: 'text-red-700' },
  ACKNOWLEDGED: { label: 'Acknowledged',           bg: 'bg-amber-100',  text: 'text-amber-700' },
  RESOLVED:     { label: 'Resolved',               bg: 'bg-green-100',  text: 'text-green-700' },
  PENDING_CONF: { label: 'Awaiting employee confirmation', bg: 'bg-blue-100', text: 'text-blue-700' },
}

// ─────────────────────────────────────────────────────────────────
// DEPOT / SITE DEFINITIONS
// ─────────────────────────────────────────────────────────────────
export const SITES = {
  SITE_SC:  { id: 'SITE_SC',  label: 'Sunshine Coast', depot: 'Caloundra, QLD',  region: 'Sunshine Coast, Noosa, Maleny, Hinterland' },
  SITE_BNE: { id: 'SITE_BNE', label: 'Brisbane',       depot: 'Ormeau, QLD',     region: 'Brisbane metro, Logan, Ipswich, Gold Coast' },
}

// ─────────────────────────────────────────────────────────────────
// DISTANCE MATRIX (pre-loaded — no Google Maps API required)
// Sources: Google Maps, TravelMath.com (verified June 2026)
// ─────────────────────────────────────────────────────────────────
export const MOCK_DISTANCE_MATRIX = [
  { id: 'dm1',  from: 'Caloundra QLD 4551',    to: 'Ormeau QLD 4208',           fromDepot: 'SITE_SC',  toDepot: 'SITE_BNE', km: 93,  mins: 70,  fmStatus: 'YELLOW',   note: 'Within SEQ corridor — single-day return possible' },
  { id: 'dm2',  from: 'Caloundra QLD 4551',    to: 'Brisbane CBD QLD 4000',     fromDepot: 'SITE_SC',  toDepot: null,       km: 103, mins: 75,  fmStatus: 'AMBER',    note: 'FM consideration required — accommodation should be offered for multi-day' },
  { id: 'dm3',  from: 'Caloundra QLD 4551',    to: 'Broadbeach QLD 4218',       fromDepot: 'SITE_SC',  toDepot: null,       km: 145, mins: 100, fmStatus: 'RED',      note: 'FM flag — accommodation mandatory for any cross-region assignment here' },
  { id: 'dm4',  from: 'Ormeau QLD 4208',       to: 'Brisbane CBD QLD 4000',     fromDepot: 'SITE_BNE', toDepot: null,       km: 51,  mins: 35,  fmStatus: 'OK',       note: 'Normal daily commute — no FM flag' },
  { id: 'dm5',  from: 'Ormeau QLD 4208',       to: 'Caloundra QLD 4551',        fromDepot: 'SITE_BNE', toDepot: 'SITE_SC',  km: 93,  mins: 70,  fmStatus: 'YELLOW',   note: 'FM consideration required for extended assignments' },
  { id: 'dm6',  from: 'Ormeau QLD 4208',       to: 'Broadbeach QLD 4218',       fromDepot: 'SITE_BNE', toDepot: null,       km: 57,  mins: 45,  fmStatus: 'YELLOW',   note: 'Borderline — monitor consecutive days' },
  { id: 'dm7',  from: 'Caloundra QLD 4551',    to: 'Robertson QLD 4109',        fromDepot: 'SITE_SC',  toDepot: null,       km: 98,  mins: 72,  fmStatus: 'AMBER',    note: 'Cross-region — close to AMBER threshold, accommodation for 3+ days' },
  { id: 'dm8',  from: 'Caloundra QLD 4551',    to: 'Annerley QLD 4103',         fromDepot: 'SITE_SC',  toDepot: null,       km: 103, mins: 75,  fmStatus: 'RED',      note: 'Cross-region >100km — RED if 2+ consecutive days, accommodation mandatory' },
  { id: 'dm9',  from: 'West End QLD 4101',     to: 'Robertson QLD 4109',        fromDepot: 'SITE_BNE', toDepot: null,       km: 10,  mins: 18,  fmStatus: 'OK',       note: 'Normal daily commute' },
  { id: 'dm10', from: 'West End QLD 4101',     to: 'Eight Mile Plains QLD 4113',fromDepot: 'SITE_BNE', toDepot: null,       km: 24,  mins: 30,  fmStatus: 'OK',       note: 'Normal daily commute' },
  { id: 'dm11', from: 'West End QLD 4101',     to: 'Broadbeach QLD 4218',       fromDepot: 'SITE_BNE', toDepot: null,       km: 65,  mins: 52,  fmStatus: 'YELLOW',   note: '50–100km range — AMBER if 3+ consecutive days' },
  { id: 'dm12', from: 'Annerley QLD 4103',     to: 'Robertson QLD 4109',        fromDepot: 'SITE_BNE', toDepot: null,       km: 6,   mins: 12,  fmStatus: 'OK',       note: 'Same suburban area — no flag' },
  { id: 'dm13', from: 'Moorooka QLD 4105',     to: 'Annerley QLD 4103',         fromDepot: 'SITE_BNE', toDepot: null,       km: 4,   mins: 10,  fmStatus: 'OK',       note: 'Adjacent suburbs — no flag' },
  { id: 'dm14', from: 'Rocklea QLD 4106',      to: 'Annerley QLD 4103',         fromDepot: 'SITE_BNE', toDepot: null,       km: 3,   mins: 8,   fmStatus: 'OK',       note: 'Adjacent suburbs — no flag' },
  { id: 'dm15', from: 'Highgate Hill QLD 4101',to: 'Eight Mile Plains QLD 4113',fromDepot: 'SITE_BNE', toDepot: null,       km: 20,  mins: 25,  fmStatus: 'OK',       note: 'Normal daily commute' },
]

const DIST_STATUS_META: Record<string, { bg: string; text: string; label: string }> = {
  OK:     { bg: 'bg-green-100',  text: 'text-green-700',  label: 'OK — no flag' },
  YELLOW: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Yellow — consider' },
  AMBER:  { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Amber — FM review' },
  RED:    { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Red — accom required' },
}
export { DIST_STATUS_META }

// ─────────────────────────────────────────────────────────────────
// EMPLOYEE FM PROFILES
// Extends existing MOCK_EMPLOYEES with FM-specific fields
// ─────────────────────────────────────────────────────────────────
export const EMPLOYEE_FM_PROFILES: Record<string, {
  homeSuburb: string
  assignedDepot: 'SITE_SC' | 'SITE_BNE'
  fmConsentOnFile: boolean
  fmRiskProfile: 'STANDARD' | 'ELEVATED' | 'MANAGED'
  accommodationPreference: 'Company books' | 'Own arrangement' | 'No preference'
  lastFMReviewDate: string | null
  drivingLicenceClass: string
}> = {
  e1: { homeSuburb: 'Annerley QLD 4103',      assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'Company books',   lastFMReviewDate: '2026-02-15', drivingLicenceClass: 'C' },
  e2: { homeSuburb: 'Woolloongabba QLD 4102',  assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'No preference',    lastFMReviewDate: '2026-01-20', drivingLicenceClass: 'C' },
  e3: { homeSuburb: 'Caloundra QLD 4551',      assignedDepot: 'SITE_SC',  fmConsentOnFile: true,  fmRiskProfile: 'MANAGED',  accommodationPreference: 'Company books',   lastFMReviewDate: '2026-06-01', drivingLicenceClass: 'C' },
  e4: { homeSuburb: 'Rocklea QLD 4106',        assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'Own arrangement', lastFMReviewDate: '2026-03-10', drivingLicenceClass: 'C' },
  e5: { homeSuburb: 'Woolloongabba QLD 4102',  assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'No preference',    lastFMReviewDate: '2025-12-15', drivingLicenceClass: 'C' },
  e6: { homeSuburb: 'West End QLD 4101',       assignedDepot: 'SITE_BNE', fmConsentOnFile: false, fmRiskProfile: 'STANDARD', accommodationPreference: 'Company books',   lastFMReviewDate: null,         drivingLicenceClass: 'C' },
  e7: { homeSuburb: 'West End QLD 4101',       assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'No preference',    lastFMReviewDate: '2026-04-22', drivingLicenceClass: 'C' },
  e8: { homeSuburb: 'Highgate Hill QLD 4101',  assignedDepot: 'SITE_BNE', fmConsentOnFile: true,  fmRiskProfile: 'STANDARD', accommodationPreference: 'No preference',    lastFMReviewDate: '2026-05-30', drivingLicenceClass: 'C' },
}

// ─────────────────────────────────────────────────────────────────
// FM FLAGS
// Today: 2026-06-17
// ─────────────────────────────────────────────────────────────────
export const MOCK_FM_FLAGS = [
  {
    id: 'fm1',
    employeeId: 'e3', employeeName: 'Sam Nguyen',
    jobId: 'j4', jobNumber: 'TP-2026-004', jobSite: 'Annerley QLD 4103',
    jobDescription: 'Energex HV Cable Replacement — Annerley',
    homeSuburb: 'Caloundra QLD 4551', assignedDepot: 'SITE_SC',
    distanceKm: 103, driveMins: 75, crossRegion: true,
    consecutiveDays: 11, shiftType: 'DAY',
    riskLevel: 'RED' as const,
    raisedAt: '2026-06-19T07:00:00Z',
    factors: [
      'Cross-region assignment (SITE_SC → Brisbane metro)',
      'Distance >100 km one-way (103 km, ~75 min)',
      '11 consecutive days away from home depot',
    ],
    pmAcknowledgedAt: '2026-06-19T08:30:00Z', pmAcknowledgedBy: 'Jane PM',
    pmAction: 'ACCOMMODATION_ARRANGED',
    accommodationId: 'acc1',
    employeeConfirmedAt: '2026-06-19T09:00:00Z',
    status: 'RESOLVED',
    notes: 'Sam travelling to Brisbane from Sunshine Coast for 11-day HV cable project. Accommodation arranged at Woolloongabba.',
  },
  {
    id: 'fm2',
    employeeId: 'e6', employeeName: 'Dave Fielding',
    jobId: 'j3', jobNumber: 'TP-2026-003', jobSite: 'Broadbeach QLD 4218',
    jobDescription: 'Gold Coast Hwy Ped Crossing Upgrade',
    homeSuburb: 'West End QLD 4101', assignedDepot: 'SITE_BNE',
    distanceKm: 65, driveMins: 52, crossRegion: false,
    consecutiveDays: 3, shiftType: 'DAY',
    riskLevel: 'AMBER' as const,
    raisedAt: '2026-06-25T10:00:00Z',
    factors: [
      'Distance 50–100 km one-way (65 km, ~52 min)',
      '3 consecutive days — accommodation must be offered',
    ],
    pmAcknowledgedAt: null, pmAcknowledgedBy: null,
    pmAction: null,
    accommodationId: 'acc2',
    employeeConfirmedAt: null,
    status: 'PENDING_CONF',
    notes: 'Apprentice first away assignment. Accommodation booked — awaiting Dave to confirm receipt.',
  },
  {
    id: 'fm3',
    employeeId: 'e7', employeeName: 'Mike Torres',
    jobId: 'j2', jobNumber: 'TP-2026-002', jobSite: 'Eight Mile Plains QLD 4113',
    jobDescription: 'Pacific Mwy On-ramp Signal Install',
    homeSuburb: 'West End QLD 4101', assignedDepot: 'SITE_BNE',
    distanceKm: 24, driveMins: 30, crossRegion: false,
    consecutiveDays: 5, shiftType: 'DAY',
    riskLevel: 'YELLOW' as const,
    raisedAt: '2026-06-16T07:00:00Z',
    factors: [
      'Approaching 6-consecutive-day limit (Day 5 of 6 maximum)',
      'Mandatory rest day required after tomorrow',
    ],
    pmAcknowledgedAt: '2026-06-16T08:15:00Z', pmAcknowledgedBy: 'Jane PM',
    pmAction: 'ACKNOWLEDGED',
    accommodationId: null,
    employeeConfirmedAt: null,
    status: 'ACKNOWLEDGED',
    notes: 'PM acknowledged. Mike to have rest day on Saturday 22 Jun. Monitor roster closely.',
  },
]

// ─────────────────────────────────────────────────────────────────
// ACCOMMODATION RECORDS
// ─────────────────────────────────────────────────────────────────
export const ACCOMMODATION_STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  ACTIVE:    { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700' },
  UPCOMING:  { label: 'Upcoming',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  COMPLETED: { label: 'Completed', bg: 'bg-gray-100',   text: 'text-gray-500' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-red-100',    text: 'text-red-700' },
}

export const MOCK_ACCOMMODATION = [
  {
    id: 'acc1',
    fmFlagId: 'fm1',
    employeeId: 'e3', employeeName: 'Sam Nguyen',
    jobId: 'j4', jobNumber: 'TP-2026-004',
    accommodationName: 'The Woolstore Apartments',
    address: '88 Ipswich Rd, Woolloongabba QLD 4102',
    bookingReference: 'TWA-2026-0142',
    checkIn: '2026-06-20', checkOut: '2026-07-31',
    nightlyRateAud: 95, totalNights: 41,
    bookedByName: 'Jane PM', bookedAt: '2026-06-19T08:30:00Z',
    employeeConfirmed: true,  employeeConfirmedAt: '2026-06-19T09:00:00Z',
    status: 'ACTIVE',
    notes: 'Ground floor preferred. Secure parking on site. Includes kitchenette.',
    riskLevel: 'RED',
  },
  {
    id: 'acc2',
    fmFlagId: 'fm2',
    employeeId: 'e6', employeeName: 'Dave Fielding',
    jobId: 'j3', jobNumber: 'TP-2026-003',
    accommodationName: 'Quest Broadbeach',
    address: '2685 Gold Coast Highway, Broadbeach QLD 4218',
    bookingReference: 'QB-2026-0058',
    checkIn: '2026-07-01', checkOut: '2026-07-03',
    nightlyRateAud: 145, totalNights: 2,
    bookedByName: 'Jane PM', bookedAt: '2026-06-25T10:30:00Z',
    employeeConfirmed: false, employeeConfirmedAt: null,
    status: 'UPCOMING',
    notes: "Dave's first away assignment. PM to call and confirm receipt.",
    riskLevel: 'AMBER',
  },
]

// ─────────────────────────────────────────────────────────────────
// AWAY REGISTER — employees currently away from home depot
// ─────────────────────────────────────────────────────────────────
export const MOCK_AWAY_REGISTER = [
  {
    employeeId: 'e3', employeeName: 'Sam Nguyen',
    homeDepot: 'SITE_SC', homeSuburb: 'Caloundra QLD 4551',
    currentJobId: 'j4', currentJobNumber: 'TP-2026-004',
    currentSite: 'Annerley QLD 4103',
    deploymentStart: '2026-06-20', deploymentEnd: '2026-07-31',
    consecutiveDays: 11, maxConsecutiveDays: 6,  // 6 is the threshold — Sam is on extended managed assignment
    accommodationId: 'acc1',
    fmFlagId: 'fm1',
    riskLevel: 'RED',
    daysFromHome: 11,
    daysRemaining: 44,  // 31 Jul - 17 Jun = 44 days remaining
    note: 'MANAGED risk profile — under continuous FM monitoring. Weekly PM check-in required.',
  },
]

// ─────────────────────────────────────────────────────────────────
// FM COMPLIANCE SUMMARY (for Reports / Dashboard)
// ─────────────────────────────────────────────────────────────────
export const FM_COMPLIANCE_SUMMARY = {
  openFlagsRed:         MOCK_FM_FLAGS.filter(f => f.riskLevel === 'RED'    && f.status === 'OPEN').length,
  openFlagsAmber:       MOCK_FM_FLAGS.filter(f => f.riskLevel === 'AMBER'  && ['OPEN','PENDING_CONF'].includes(f.status)).length,
  openFlagsYellow:      MOCK_FM_FLAGS.filter(f => f.riskLevel === 'YELLOW' && f.status === 'OPEN').length,
  employeesAwayCount:   MOCK_AWAY_REGISTER.length,
  approaching6DayLimit: MOCK_FM_FLAGS.filter(f => f.consecutiveDays >= 5 && f.consecutiveDays < 6).length,
  accommodationExpiring48h: MOCK_ACCOMMODATION.filter(a => {
    const checkOut = new Date(a.checkOut)
    const in48h    = new Date('2026-06-19')  // 17 Jun + 2 days
    return checkOut <= in48h && a.status === 'ACTIVE'
  }).length,
  overdueReviews: Object.entries({
    e5: '2025-12-15',   // >6 months ago
    e6: null,           // never reviewed
  }).length,
  activeAccommodation: MOCK_ACCOMMODATION.filter(a => a.status === 'ACTIVE').length,
  upcomingAccommodation: MOCK_ACCOMMODATION.filter(a => a.status === 'UPCOMING').length,
}

// ─────────────────────────────────────────────────────────────────
// HELPER: calculate FM risk level from inputs
// ─────────────────────────────────────────────────────────────────
export function calcFMRisk(params: {
  distanceKm: number
  crossRegion: boolean
  consecutiveDays: number
  nightShiftNextDayEarlyStart?: boolean
}): { level: 'GREEN'|'YELLOW'|'AMBER'|'RED'; factors: string[] } {
  const { distanceKm, crossRegion, consecutiveDays, nightShiftNextDayEarlyStart } = params
  const factors: string[] = []
  let level: 'GREEN'|'YELLOW'|'AMBER'|'RED' = 'GREEN'

  const escalate = (to: 'GREEN'|'YELLOW'|'AMBER'|'RED') => {
    const order = ['GREEN','YELLOW','AMBER','RED']
    if (order.indexOf(to) > order.indexOf(level)) level = to
  }

  // 6-day max rule
  if (consecutiveDays >= FM_THRESHOLDS.CONSECUTIVE_MAX) {
    factors.push(`${consecutiveDays} consecutive days — mandatory rest day required`)
    escalate('RED')
  }

  // Night shift + early start
  if (nightShiftNextDayEarlyStart) {
    factors.push('Night shift followed by early start — minimum 10hr rest required')
    escalate('AMBER')
  }

  // Distance rules
  if (distanceKm > FM_THRESHOLDS.DISTANCE_AMBER_KM) {
    // >100km
    if (consecutiveDays >= FM_THRESHOLDS.DAYS_RED || crossRegion) {
      factors.push(`Cross-region assignment >100 km (${distanceKm} km), ${consecutiveDays} consecutive days`)
      escalate('RED')
    } else {
      factors.push(`Distance >100 km one-way (${distanceKm} km) — accommodation should be offered`)
      escalate('AMBER')
    }
  } else if (distanceKm >= FM_THRESHOLDS.DISTANCE_YELLOW_KM) {
    // 50-100km
    if (consecutiveDays >= FM_THRESHOLDS.DAYS_AMBER) {
      factors.push(`${distanceKm} km one-way × ${consecutiveDays} consecutive days — AMBER threshold`)
      escalate('AMBER')
    } else {
      factors.push(`Distance ${distanceKm} km one-way — confirm travel is acceptable`)
      escalate('YELLOW')
    }
  }

  if (factors.length === 0) factors.push('All factors within normal limits')
  return { level, factors }
}
